import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SwUpdate } from '@angular/service-worker';
import { fromEvent, merge, of, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-pwa-status',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  templateUrl: './pwa-status.component.html',
  styleUrls: ['./pwa-status.component.scss']
})
export class PwaStatusComponent implements OnInit, OnDestroy {
  statusIcon = 'wifi';
  statusMessage = 'Online';
  statusClass = 'online';
  iconClass = 'icon-online';

  private destroy$ = new Subject<void>();
  private isOnline$ = merge(
    of(navigator.onLine),
    fromEvent(window, 'online').pipe(map(() => true)),
    fromEvent(window, 'offline').pipe(map(() => false))
  );

  constructor(private swUpdate: SwUpdate) {}

  ngOnInit(): void {
    this.setupOnlineStatus();
    this.setupUpdateStatus();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupOnlineStatus(): void {
    this.isOnline$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isOnline => {
        if (isOnline) {
          this.setOnlineStatus();
        } else {
          this.setOfflineStatus();
        }
      });
  }

  private setupUpdateStatus(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates
        .pipe(takeUntil(this.destroy$))
        .subscribe(event => {
          if (event.type === 'VERSION_READY') {
            this.setUpdateAvailableStatus();
          }
        });
    }
  }

  private setOnlineStatus(): void {
    this.statusIcon = 'wifi';
    this.statusMessage = 'Online - App is connected';
    this.statusClass = 'online';
    this.iconClass = 'icon-online';
  }

  private setOfflineStatus(): void {
    this.statusIcon = 'wifi_off';
    this.statusMessage = 'Offline - Some features may be limited';
    this.statusClass = 'offline';
    this.iconClass = 'icon-offline';
  }

  private setUpdateAvailableStatus(): void {
    this.statusIcon = 'system_update_alt';
    this.statusMessage = 'Update available - Click to refresh';
    this.statusClass = 'update-available';
    this.iconClass = 'icon-update';
  }
}
